import {
  AnalystChartSpec,
  AnalystTableSpec,
  ratingBounds,
} from '@digital-twins/shared-types'

import {
  DENSE_CUT_MIN_CUTS,
  DENSE_CUT_MIN_GROUPS,
  isDrawableAnalystPlot,
} from '@/utils/analystChartPlot'

export { DENSE_CUT_MIN_CUTS, DENSE_CUT_MIN_GROUPS }

/**
 * Featured-plot shape routing.
 *
 * Cache scope: one-shot, not cached. Pure spec inspection; no I/O.
 *
 * First matching row wins. Unmapped valid specs fall through to Amitai's
 * AnalystChart / AnalystTable — never a blank. Invalid specs become text.
 *
 * Plots wave 6: every chart without a scale distribution draws through
 * AnalystChart (Python's plot, else the stored read in
 * `legacy/analystChartAdapters`): a crosstab as grouped bars, a dense cut as
 * a heat grid. A dense table draws Python's heat-grid plot, else the stored
 * read (`legacy/analystTableAdapters`).
 *
 * Study-tab findings can call `routeAnalystChart` / `routeAnalystTable`
 * unchanged; adopting that is Amitai's call, not this step.
 */

export type FeaturedPlotShape =
  | 'plot'
  | 'scale_distribution'
  | 'crosstab'
  | 'dense_cut'
  | 'unmapped'
  | 'invalid'

export type FeaturedPlotRenderer =
  | 'analyst_plot'
  | 'diverging_stacked'
  | 'analyst_chart'
  | 'analyst_table'
  | 'text'

export interface FeaturedPlotRoute {
  shape: FeaturedPlotShape
  renderer: FeaturedPlotRenderer
  when: string
}

export const FEATURED_PLOT_ROUTING_TABLE: Array<FeaturedPlotRoute> = [
  {
    shape: 'invalid',
    renderer: 'text',
    when: 'spec fails analyst-chart.v1 / analyst-table.v1 validation',
  },
  {
    shape: 'plot',
    renderer: 'analyst_plot',
    when: 'spec carries a drawable Python plot (routed by plot.kind; a table plot is a dense heat grid)',
  },
  {
    shape: 'scale_distribution',
    renderer: 'diverging_stacked',
    when: 'stacked_bar whose series carries a declared scale (scaleMin/scaleMax first, then scaleUnit) and whose categories are its points',
  },
  {
    shape: 'dense_cut',
    renderer: 'analyst_chart',
    when: 'stored bar or stacked_bar with at least 4 groups and 4 cuts: heat grid (stored read)',
  },
  {
    shape: 'dense_cut',
    renderer: 'analyst_plot',
    when: 'stored table with at least 4 rows and 4 columns, every column scaled: heat grid (stored read)',
  },
  {
    shape: 'crosstab',
    renderer: 'analyst_chart',
    when: 'stored bar with two or more series: grouped bars (shares) or a plain grid',
  },
  {
    shape: 'unmapped',
    renderer: 'analyst_chart',
    when: 'any other valid AnalystChartSpec',
  },
  {
    shape: 'unmapped',
    renderer: 'analyst_table',
    when: 'any other valid AnalystTableSpec',
  },
]

export interface FeaturedChartDecision {
  shape: FeaturedPlotShape
  renderer: FeaturedPlotRenderer
}

/**
 * The scale points of a rating distribution, read from its typed signal only:
 * the Python-stamped declared scale on the first series (`scaleMin` /
 * `scaleMax`, any declared scale; else the `scaleUnit` bounds), and categories
 * that are integer points inside those ends. Never parsed from label words.
 * Null when the chart is not a typed scale distribution (no declared scale:
 * plain bars).
 */
export function typedScalePoints(
  chart: AnalystChartSpec,
): { min: number; max: number; points: Array<number> } | null {
  const bounds = declaredScaleBounds(chart.series[0])
  if (!bounds) {
    return null
  }
  const [min, max] = bounds
  const points = chartCategories(chart).map((category) =>
    /^-?\d+$/.test(category.trim()) ? Number(category.trim()) : Number.NaN,
  )
  if (
    points.length < 2 ||
    points.some((point) => !Number.isInteger(point) || point < min || point > max)
  ) {
    return null
  }
  return { min, max, points }
}

function declaredScaleBounds(series: AnalystChartSpec['series'][number] | undefined): [number, number] | null {
  if (!series) {
    return null
  }
  const { scaleMin, scaleMax, scaleUnit } = series
  if (typeof scaleMin === 'number' && typeof scaleMax === 'number' && scaleMax > scaleMin) {
    return [scaleMin, scaleMax]
  }
  if (scaleUnit) {
    const [min, max] = ratingBounds(scaleUnit)
    return [min, max]
  }
  return null
}

export function isValidAnalystChart(chart: AnalystChartSpec): boolean {
  if (
    chart.schemaVersion !== 'analyst-chart.v1' ||
    !Array.isArray(chart.series) ||
    chart.series.length === 0
  ) {
    return false
  }

  if (!Array.isArray(chart.series[0].data)) {
    return false
  }

  const categories = chart.series[0].data.map((datum) => datum.category)
  const hasValidSeries = chart.series.every(
    (series) =>
      Array.isArray(series.data) &&
      series.data.length === categories.length &&
      series.data.every(
        (datum, index) =>
          typeof datum.category === 'string' &&
          datum.category === categories[index] &&
          (datum.value === null ||
            (typeof datum.value === 'number' && Number.isFinite(datum.value))) &&
          typeof datum.baseN === 'number' &&
          Number.isFinite(datum.baseN) &&
          typeof datum.isSuppressed === 'boolean',
      ),
  )

  if (!hasValidSeries) {
    return false
  }

  return chart.chartType !== 'donut' || chart.series.length === 1
}

export function isValidAnalystTable(table: AnalystTableSpec): boolean {
  if (
    table.schemaVersion !== 'analyst-table.v1' ||
    !Array.isArray(table.columns) ||
    table.columns.length === 0 ||
    table.columns.length > 50 ||
    !Array.isArray(table.rows) ||
    table.rows.length === 0 ||
    table.rows.length > 200
  ) {
    return false
  }

  const columnIds = table.columns.map((column) => column.id)
  const rowIds = table.rows.map((row) => row.id)
  if (
    new Set(columnIds).size !== columnIds.length ||
    new Set(rowIds).size !== rowIds.length ||
    table.columns.some((column) => !column.id || !column.label || !column.unit)
  ) {
    return false
  }

  const referencedFactIds = table.rows.flatMap((row) =>
    row.cells.map((cell) => cell.evidenceFactId),
  )
  const referencedFactIdSet = new Set(referencedFactIds)
  const evidenceFactIdSet = new Set(table.evidenceFactIds)
  if (
    evidenceFactIdSet.size !== table.evidenceFactIds.length ||
    referencedFactIdSet.size !== referencedFactIds.length ||
    referencedFactIds.length !== table.evidenceFactIds.length ||
    referencedFactIds.some((factId) => !evidenceFactIdSet.has(factId))
  ) {
    return false
  }

  return table.rows.every((row) => {
    const cellsByColumn = new Map(row.cells.map((cell) => [cell.columnId, cell]))
    return (
      row.id.length > 0 &&
      row.label.length > 0 &&
      row.cells.length === columnIds.length &&
      cellsByColumn.size === columnIds.length &&
      columnIds.every((columnId) => {
        const cell = cellsByColumn.get(columnId)
        return (
          cell !== undefined &&
          cell.evidenceFactId.length > 0 &&
          Number.isInteger(cell.baseN) &&
          cell.baseN >= 0 &&
          (cell.suppressionThreshold === undefined ||
            (Number.isInteger(cell.suppressionThreshold) &&
              cell.suppressionThreshold >= 0)) &&
          (cell.baseN >= (cell.suppressionThreshold ?? 8) ||
            cell.isSuppressed) &&
          (cell.isSuppressed
            ? cell.value === null
            : typeof cell.value === 'number' && Number.isFinite(cell.value))
        )
      })
    )
  })
}

export function chartCategories(chart: AnalystChartSpec): Array<string> {
  return chart.series[0]?.data.map((datum) => datum.category) ?? []
}

export function isScaleDistributionChart(chart: AnalystChartSpec): boolean {
  if (chart.chartType !== 'stacked_bar') {
    return false
  }
  return typedScalePoints(chart) !== null
}

export function isDenseCutChart(chart: AnalystChartSpec): boolean {
  if (chart.chartType !== 'bar' && chart.chartType !== 'stacked_bar') {
    return false
  }
  const categories = chartCategories(chart)
  return (
    chart.series.length >= DENSE_CUT_MIN_GROUPS &&
    categories.length >= DENSE_CUT_MIN_CUTS
  )
}

export function isCrosstabChart(chart: AnalystChartSpec): boolean {
  return chart.chartType === 'bar' && chart.series.length >= 2
}

export function isDenseCutTable(table: AnalystTableSpec): boolean {
  return (
    table.rows.length >= DENSE_CUT_MIN_GROUPS &&
    table.columns.length >= DENSE_CUT_MIN_CUTS
  )
}

export function routeAnalystChart(chart: AnalystChartSpec): FeaturedChartDecision {
  if (!isValidAnalystChart(chart)) {
    return { shape: 'invalid', renderer: 'text' }
  }
  if (isDrawableAnalystPlot(chart.plot)) {
    return { shape: 'plot', renderer: 'analyst_plot' }
  }
  if (isScaleDistributionChart(chart)) {
    return { shape: 'scale_distribution', renderer: 'diverging_stacked' }
  }
  if (isDenseCutChart(chart)) {
    return { shape: 'dense_cut', renderer: 'analyst_chart' }
  }
  if (isCrosstabChart(chart)) {
    return { shape: 'crosstab', renderer: 'analyst_chart' }
  }
  return { shape: 'unmapped', renderer: 'analyst_chart' }
}

export function routeAnalystTable(table: AnalystTableSpec): FeaturedChartDecision {
  if (!isValidAnalystTable(table)) {
    return { shape: 'invalid', renderer: 'text' }
  }
  if (isDrawableAnalystPlot(table.plot)) {
    return { shape: 'plot', renderer: 'analyst_plot' }
  }
  if (isDenseCutTable(table)) {
    return { shape: 'dense_cut', renderer: 'analyst_plot' }
  }
  return { shape: 'unmapped', renderer: 'analyst_table' }
}
